import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ALL_SCALES,
  FOCUS_SUGGESTIONS,
  PROGRESSION_OPTIONS,
  RatingScale,
} from '../../data/apneaScales';
import { POST_EVENT_RULES } from '../../data/blackoutSigns';
import { formatSeconds, parseDuration } from '../../utils/apneaTables';
import { RecordedSet } from '../../hooks/useApneaTimer';
import {
  ApiDecision,
  ApiTimeSource,
  ApiWarningKind,
  ApneaSessionDto,
} from '../../services/apneaService';

/**
 * Post-session log. The ratings are the point: the training sources treat recovery
 * quality and relaxation as the real progression signals, not hold duration.
 *
 * Every recorded hold stays editable here, with provenance tracked — if the app was
 * backgrounded, killed, or the user simply forgot to stop the timer, the round is
 * corrected rather than lost.
 */

export interface PostSessionValues {
  metrics: {
    recovery?: number;
    relaxation?: number;
    technique?: number;
    perceivedEffort?: number;
  };
  focus: string;
  observations: string;
  takeaway: string;
  nextFocus: string;
  warningSigns: { occurred: boolean; kind: ApiWarningKind; description: string };
  decision: ApiDecision | '';
  sets: Array<RecordedSet & { source: ApiTimeSource }>;
}

interface PostSessionLogProps {
  records: RecordedSet[];
  aborted: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: (values: PostSessionValues) => void;
  onDiscard: () => void;
}

const ScaleField: React.FC<{
  scale: RatingScale;
  value?: number;
  onChange: (value: number) => void;
}> = ({ scale, value, onChange }) => (
  <FormControl component="fieldset" sx={{ display: 'block', mb: 2 }}>
    <FormLabel sx={{ fontWeight: 600 }}>{scale.name}</FormLabel>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {scale.purpose}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {scale.options.map(option => (
        <Tooltip key={option.value} title={option.description} arrow>
          <Chip
            label={`${option.value} · ${option.label}`}
            onClick={() => onChange(option.value)}
            color={value === option.value ? 'primary' : 'default'}
            variant={value === option.value ? 'filled' : 'outlined'}
            size="small"
          />
        </Tooltip>
      ))}
    </Stack>
  </FormControl>
);

const PostSessionLog: React.FC<PostSessionLogProps> = ({
  records,
  aborted,
  saving = false,
  error = null,
  onSave,
  onDiscard,
}) => {
  const [values, setValues] = useState<PostSessionValues>({
    metrics: {},
    focus: '',
    observations: '',
    takeaway: '',
    nextFocus: '',
    warningSigns: { occurred: false, kind: 'None', description: '' },
    decision: '',
    sets: records.map(r => ({ ...r, source: 'Timed' as ApiTimeSource })),
  });

  const [holdText, setHoldText] = useState<Record<number, string>>(
    Object.fromEntries(records.map(r => [r.set, formatSeconds(r.holdSeconds)]))
  );

  const setMetric = (id: string, value: number) =>
    setValues(v => ({ ...v, metrics: { ...v.metrics, [id]: value } }));

  const commitHold = (setNumber: number) => {
    const parsed = parseDuration(holdText[setNumber] ?? '');
    if (parsed === null) {
      // Restore the last good value rather than leaving an unparseable field.
      const original = values.sets.find(s => s.set === setNumber);
      setHoldText(t => ({ ...t, [setNumber]: formatSeconds(original?.holdSeconds ?? 0) }));
      return;
    }
    setValues(v => ({
      ...v,
      sets: v.sets.map(s => (s.set === setNumber
        ? { ...s, holdSeconds: parsed, source: s.holdSeconds === parsed ? s.source : 'Corrected' }
        : s)),
    }));
  };

  const canSave = values.metrics.recovery !== undefined && values.decision !== '';

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>
        {aborted ? 'Session ended early' : 'Session complete'}
      </Typography>
      {aborted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ending early is a normal outcome. What you completed is still worth logging.
        </Alert>
      )}

      {/* Recorded sets, each correctable. */}
      {values.sets.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Recorded holds</Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {values.sets.map(s => (
              <Stack key={s.set} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 52 }}>set {s.set}</Typography>
                <TextField
                  size="small"
                  value={holdText[s.set] ?? ''}
                  onChange={e => setHoldText(t => ({ ...t, [s.set]: e.target.value }))}
                  onBlur={() => commitHold(s.set)}
                  sx={{ width: 110 }}
                />
                {s.firstContractionAt !== null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`contraction ${formatSeconds(s.firstContractionAt)}`}
                  />
                )}
                {s.endedEarly && <Chip size="small" color="info" label="ended early" />}
                {s.source === 'Corrected' && <Chip size="small" label="corrected" />}
              </Stack>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Any hold can be corrected here — a wrong number you cannot fix is worse than no number.
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {ALL_SCALES.map(scale => (
        <ScaleField
          key={scale.id}
          scale={scale}
          value={values.metrics[scale.id as keyof typeof values.metrics]}
          onChange={v => setMetric(scale.id, v)}
        />
      ))}

      <Divider sx={{ my: 2 }} />

      <Stack spacing={2}>
        <Autocomplete
          freeSolo
          options={FOCUS_SUGGESTIONS}
          value={values.focus}
          onInputChange={(_e, v) => setValues(s => ({ ...s, focus: v }))}
          renderInput={params => (
            <TextField
              {...params}
              label="Today's one skill"
              helperText="One primary objective per session; everything else is maintenance."
            />
          )}
        />

        <TextField
          label="Observations"
          value={values.observations}
          onChange={e => setValues(v => ({ ...v, observations: e.target.value }))}
          multiline
          minRows={2}
          helperText="What felt smooth? Where did tension appear? How did recovery feel?"
        />

        <TextField
          label="One takeaway"
          value={values.takeaway}
          onChange={e => setValues(v => ({ ...v, takeaway: e.target.value }))}
        />

        <TextField
          label="Next session focus"
          value={values.nextFocus}
          onChange={e => setValues(v => ({ ...v, nextFocus: e.target.value }))}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Warning signs drive the post-event blocks, so this is asked plainly. */}
      <FormControlLabel
        control={
          <Switch
            checked={values.warningSigns.occurred}
            onChange={e => setValues(v => ({
              ...v,
              warningSigns: {
                ...v.warningSigns,
                occurred: e.target.checked,
                kind: e.target.checked ? 'Other' : 'None',
              },
            }))}
          />
        }
        label="I noticed warning signs during this session"
      />

      {values.warningSigns.occurred && (
        <Box sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            label="What happened"
            value={values.warningSigns.kind}
            onChange={e => setValues(v => ({
              ...v,
              warningSigns: { ...v.warningSigns, kind: e.target.value as ApiWarningKind },
            }))}
            sx={{ mb: 1 }}
          >
            <MenuItem value="Other">Other warning sign (dizziness, tingling, tunnel vision)</MenuItem>
            <MenuItem value="Lmc">Loss of motor control / samba</MenuItem>
            <MenuItem value="Blackout">Blackout</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Describe it"
            value={values.warningSigns.description}
            onChange={e => setValues(v => ({
              ...v,
              warningSigns: { ...v.warningSigns, description: e.target.value },
            }))}
            multiline
            minRows={2}
          />
          {(values.warningSigns.kind === 'Lmc' || values.warningSigns.kind === 'Blackout') && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {POST_EVENT_RULES.nauiSameDayStop.rule} {' '}
              {POST_EVENT_RULES.conservativeTablePause.rule} Logging this will block
              further breath-hold training accordingly.
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <FormControl component="fieldset">
        <FormLabel sx={{ fontWeight: 600 }}>Progression decision</FormLabel>
        <RadioGroup
          value={values.decision}
          onChange={e => setValues(v => ({ ...v, decision: e.target.value as ApiDecision }))}
        >
          {PROGRESSION_OPTIONS.map(option => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={() => onSave(values)}
          disabled={!canSave || saving}
        >
          {saving ? 'Saving…' : 'Save session'}
        </Button>
        <Button onClick={onDiscard} disabled={saving}>Discard</Button>
      </Stack>
      {!canSave && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Recovery rating and a progression decision are required.
        </Typography>
      )}
    </Paper>
  );
};

export default PostSessionLog;

/** Build the API payload from the form values and the session context. */
export const buildSessionPayload = (
  values: PostSessionValues,
  context: {
    type: ApneaSessionDto['type'];
    startedAt: Date;
    completedAt: Date;
    phase?: number;
    plannedSets: Array<{ set: number; holdSeconds: number; ventSeconds: number }>;
    aborted: boolean;
    abortReason?: string;
    planVersion?: number;
  }
) => ({
  type: context.type,
  startedAt: context.startedAt.toISOString(),
  completedAt: context.completedAt.toISOString(),
  phase: context.phase ?? null,
  planVersion: context.planVersion ?? null,
  plannedSets: context.plannedSets,
  actualSets: values.sets.map(s => ({
    set: s.set,
    holdSeconds: s.holdSeconds,
    ventSeconds: s.ventSeconds,
    firstContractionAt: s.firstContractionAt,
    endedEarly: s.endedEarly,
    recoveryBreathsCompleted: s.recoveryBreathsCompleted,
    source: s.source,
  })),
  metrics: {
    recovery: values.metrics.recovery ?? null,
    relaxation: values.metrics.relaxation ?? null,
    technique: values.metrics.technique ?? null,
    perceivedEffort: values.metrics.perceivedEffort ?? null,
  },
  focus: values.focus || null,
  observations: values.observations || null,
  takeaway: values.takeaway || null,
  nextFocus: values.nextFocus || null,
  warningSigns: {
    occurred: values.warningSigns.occurred,
    kind: values.warningSigns.kind,
    description: values.warningSigns.description || null,
  },
  decision: values.decision === '' ? null : values.decision,
  aborted: context.aborted,
  abortReason: context.abortReason ?? null,
});
